import { cfApi } from "./client"
import type { DealDetails, DealListResponse } from "./deals-types"

/**
 * Deals API wrapper functions
 * Provides type-safe access to deal management endpoints
 */
export const dealsApi = {
  /**
   * Get deal details by ID
   */
  async getDetails(dealId: string): Promise<DealDetails> {
    return await cfApi.get<DealDetails>(`/api/deals/${dealId}`)
  },
  async list(): Promise<DealListResponse> {
    return await cfApi.get<DealListResponse>(`/api/deals/list`)
  },
  /**
   * Add processing request templates to a deal
   */
  async addTemplatesToDeal(
    dealId: string,
    templateIds: string[],
    contactAssignments?: Record<string, string[]>
  ) {
    return await cfApi.post<{
      success: boolean
      dealId: string
      results: Array<{
        templateId: string
        templateName: string
        requestId?: string
        created: boolean
        error?: string
      }>
      summary: {
        total: number
        created: number
        existed: number
        failed: number
      }
    }>(`/api/deals/${dealId}/add-templates`, {
      templateIds,
      ...(contactAssignments && { contactAssignments }),
    })
  },

  /**
   * Create an ad-hoc processing request for a deal
   */
  async createProcessingRequest(
    dealId: string,
    data: {
      name: string
      type: string
      instructions?: string
      url?: string
      milestone?: string
      processor_notes?: string
      acknowledgement_file?: string
      owner_contact_id?: string
    }
  ) {
    return await cfApi.post<{
      dealId: string
      processingRequest: {
        id: string
        properties: {
          name: string
          type: string
          instructions?: string
          url?: string
          processor_notes?: string
          borrower_notes?: string
          hs_object_id: string
          hs_pipeline_stage: string
        }
      } | null
      created: boolean
      message: string
    }>(`/api/deals/${dealId}/processing-request`, data)
  },

  /**
   * Update a processing request
   */
  async updateProcessingRequest(
    dealId: string,
    requestId: string,
    data: {
      name?: string
      type?: string
      instructions?: string
      url?: string
      milestone?: string
      processor_notes?: string
      acknowledgement_file?: string
      owner_contact_id?: string
    }
  ) {
    return await cfApi.put<{
      dealId: string
      processingRequest: {
        id: string
        properties: {
          name: string
          type: string
          instructions?: string
          url?: string
          processor_notes?: string
          borrower_notes?: string
          hs_object_id: string
          hs_pipeline_stage: string
        }
      }
      updated: boolean
      message: string
    }>(`/api/deals/${dealId}/processing-requests/${requestId}`, data)
  },

  /**
   * Delete a processing request
   */
  async deleteProcessingRequest(dealId: string, requestId: string) {
    return await cfApi.delete<{
      dealId: string
      requestId: string
      deleted: boolean
      message: string
    }>(`/api/deals/${dealId}/processing-requests/${requestId}`)
  },

  /**
   * Get HubSpot URL for an object (deal, processing_request, or request_document)
   */
  async getHubSpotUrl(
    type: "deal" | "processing_request" | "request_document",
    id: string
  ) {
    return await cfApi.get<{
      url: string
      type: string
      id: string
    }>(`/api/deals/url/${type}/${id}`)
  },

  /**
   * Rescan a document through the extraction pipeline
   */
  async rescanDocument(
    dealId: string,
    documentId: string
  ): Promise<{
    success: boolean
    pipelineResult?: {
      classification: {
        document_type: string
        document_type_name: string
        confidence: number
        reasoning: string
      }
      extraction: {
        extracted_data: Record<string, unknown>
        extraction_failed?: boolean
      }
      verification: {
        verified: boolean
        corrections: Array<{
          field: string
          original: unknown
          corrected: unknown
          reason: string
        }>
        flags: Array<{
          field: string
          issue: string
          severity: string
        }>
      } | null
      timing: {
        classify_ms: number
        extract_ms: number
        verify_ms: number
        total_ms: number
      }
    }
  }> {
    return await cfApi.post(
      `/api/deals/${dealId}/documents/${documentId}/rescan`,
      {}
    )
  },

  /**
   * Download all submitted files for a deal as zip
   */
  async downloadFiles(
    dealId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await cfApi.getRaw(`/api/deals/${dealId}/download-files`)

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || "Failed to download files")
    }

    const blob = await response.blob()

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get("content-disposition")
    let filename = `files-${dealId}.zip` // Default fallback

    if (contentDisposition) {
      // Try to extract filename from header (handles both quoted and unquoted)
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      const filenameMatch = filenameRegex.exec(contentDisposition)
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "")
      }
    }

    return { blob, filename }
  },
}
